import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { fetchOutrightsAllEventsReq, fetchOutrightsMarketCountReq, fetchTopOutrightsReq } from './services';
import { sortEventsByPriority } from 'views/outrights/utils';

const initialState = {
  // Single source of truth for all outright events (list, detail, top).
  // SignalR add/update/delete operate on this list.
  events: [],
  // Ordered list of event ids that are "top" — derived from REST fetchTopOutrights.
  topEventIds: [],
  topLoading: false,
  loading: false,
  error: null,
  selectedSportId: null,
  marketCount: 0
};

const filterActiveMarkets = (events) =>
  events.map((ev) => ({
    ...ev,
    markets: Array.isArray(ev?.markets)
      ? ev.markets.filter((m) => m?.marketStatus === 6)
      : []
  }));

const upsertOutrightEvents = (list, events) => {
  events.forEach((event) => {
    const id = event?.se?.id;
    if (!id) return;
    const idx = list.findIndex((e) => e.se?.id === id);
    if (idx === -1) {
      list.push(event);
    } else {
      list[idx] = event;
    }
  });
};

export const fetchOutrightsAllEvents = createAsyncThunk(
  'outrights/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const events = await fetchOutrightsAllEventsReq();
      return events;
    } catch (err) {
      return rejectWithValue(err?.message || 'Failed to load outrights');
    }
  }
);

export const fetchTopOutrights = createAsyncThunk(
  'outrights/fetchTop',
  async (_, { rejectWithValue }) => {
    try {
      const events = await fetchTopOutrightsReq();
      return events;
    } catch (err) {
      return rejectWithValue(err?.message || 'Failed to load top outrights');
    }
  }
);

export const fetchOutrightsMarketCount = createAsyncThunk(
  'outrights/fetchMarketCount',
  async (_, { rejectWithValue }) => {
    try {
      const count = await fetchOutrightsMarketCountReq();
      return count;
    } catch (err) {
      return rejectWithValue(err?.message || 'Failed to load outrights count');
    }
  }
);

const outrightsSlice = createSlice({
  name: 'outrights',
  initialState,
  reducers: {
    setSelectedSportId: (state, action) => {
      state.selectedSportId = action.payload;
    },
    clearOutrightsError: (state) => {
      state.error = null;
    },
    addOutrights: (state, action) => {
      const { events: raw = [] } = action.payload || {};
      upsertOutrightEvents(state.events, filterActiveMarkets(raw));
    },
    updateOutrights: (state, action) => {
      const { events: raw = [] } = action.payload || {};
      upsertOutrightEvents(state.events, filterActiveMarkets(raw));
    },
    deleteOutrights: (state, action) => {
      const { events: ids = [] } = action.payload || {};
      const idSet = new Set(ids);
      state.events = state.events.filter((e) => !idSet.has(e.se?.id));
      state.topEventIds = state.topEventIds.filter((id) => !idSet.has(id));
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOutrightsAllEvents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOutrightsAllEvents.fulfilled, (state, action) => {
        state.loading = false;
        // Merge — preserve any signalR-pushed events that the REST response doesn't include yet
        const incomingIds = new Set(action.payload.map((e) => e?.se?.id).filter(Boolean));
        const preserved = state.events.filter((e) => !incomingIds.has(e.se?.id));
        state.events = [...action.payload, ...preserved];
      })
      .addCase(fetchOutrightsAllEvents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to load outrights';
      })
      .addCase(fetchOutrightsMarketCount.fulfilled, (state, action) => {
        state.marketCount = action.payload;
      })
      .addCase(fetchTopOutrights.pending, (state) => {
        state.topLoading = true;
      })
      .addCase(fetchTopOutrights.fulfilled, (state, action) => {
        state.topLoading = false;
        upsertOutrightEvents(state.events, action.payload);
        state.topEventIds = action.payload.map((e) => e?.se?.id).filter(Boolean);
      })
      .addCase(fetchTopOutrights.rejected, (state) => {
        state.topLoading = false;
      });
  }
});

export const {
  setSelectedSportId,
  clearOutrightsError,
  addOutrights,
  updateOutrights,
  deleteOutrights
} = outrightsSlice.actions;

// Base selectors
export const selectAllEvents = (state) => state.outrights.events;
export const selectOutrightsLoading = (state) => state.outrights.loading;
export const selectOutrightsError = (state) => state.outrights.error;
export const selectSelectedSportId = (state) => state.outrights.selectedSportId;
export const selectOutrightsMarketCount = (state) => state.outrights.marketCount;
export const selectTopOutrightIds = (state) => state.outrights.topEventIds;
export const selectTopOutrightsLoading = (state) => state.outrights.topLoading;

// Derived: top events resolved from the unified store, preserving REST order.
export const selectTopOutrightEvents = createSelector(
  [selectAllEvents, selectTopOutrightIds],
  (events, ids) => {
    const byId = new Map(events.map((e) => [e.se?.id, e]));
    return ids.map((id) => byId.get(id)).filter(Boolean);
  }
);

// Memoized: returns the same array reference until events or selectedSportId change.
export const selectFilteredEvents = createSelector(
  [selectAllEvents, selectSelectedSportId],
  (events, selectedSportId) => {
    const filtered =
      selectedSportId == null
        ? events
        : events.filter((e) => e.se?.sportId === selectedSportId);
    return sortEventsByPriority(filtered);
  }
);

export const selectEventById = (state, id) =>
  state.outrights.events.find((e) => e.se?.id === id) || null;

// Memoized: returns the same array reference until events change.
export const selectAvailableSports = createSelector(
  [selectAllEvents],
  (events) => {
    const map = new Map();
    for (const e of events) {
      const sId = e.se?.sportId;
      const sName = e.se?.sportName;
      if (sId == null) continue;
      if (!map.has(sId)) map.set(sId, { sportId: sId, sportName: sName, count: 0 });
      map.get(sId).count += 1;
    }
    return Array.from(map.values()).sort((a, b) => a.sportId - b.sportId);
  }
);

export default outrightsSlice.reducer;
